// src/context/CompanyContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { supabase } from "../Services/supabaseClient";

const CompanyContext = createContext();

function normalizePlan(p) {
  const v = String(p || "free").toLowerCase();
  if (v === "basic") return "starter";
  if (v === "premium") return "sme";
  if (v === "business") return "business";
  if (["free", "starter", "sme", "business"].includes(v)) return v;
  return "free";
}

function safeDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

export function CompanyProvider({ children }) {
  const [companyId, setCompanyId] = useState(null);
  const [companyRole, setCompanyRole] = useState(null);
  const [company, setCompany] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ NEW: company-wide onboarding flag
  const [companyOnboarded, setCompanyOnboarded] = useState(false);

  // Billing state
  const [plan, setPlan] = useState("free");
  const [trialEndsAt, setTrialEndsAt] = useState(null);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);

  const [billingStatus, setBillingStatus] = useState(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState(null);
  const [billingRowMeta, setBillingRowMeta] = useState(null);

  const [businessRole, setBusinessRole] = useState(null);

  const loadContext = useCallback(async () => {
    setLoading(true);

    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError || !user) {
      console.error("Not signed in", authError);

      setCompanyId(null);
      setCompanyRole(null);
      setBusinessRole(null);
      setCompany(null);
      setMembers([]);

      setCompanyOnboarded(false);

      setPlan("free");
      setTrialEndsAt(null);
      setHasUsedTrial(false);
      setBillingStatus(null);
      setSubscriptionEnd(null);
      setBillingRowMeta(null);

      setLoading(false);
      return;
    }

    const { data: membership, error: memErr } = await supabase
      .from("company_members")
      .select("company_id, role, business_role")
      .eq("user_id", user.id)
      .maybeSingle();

    if (memErr) {
      console.error("Error fetching membership", memErr);
      setLoading(false);
      return;
    }

    if (!membership) {
      console.warn("User is not a member of any company");

      setCompanyId(null);
      setCompanyRole(null);
      setBusinessRole(null);
      setCompany(null);
      setMembers([]);

      setCompanyOnboarded(false);

      setPlan("free");
      setTrialEndsAt(null);
      setHasUsedTrial(false);
      setBillingStatus(null);
      setSubscriptionEnd(null);
      setBillingRowMeta(null);

      setLoading(false);
      return;
    }

    const { company_id } = membership;
    const role = membership.role;
    const bRole = membership.business_role || null;
    setBusinessRole(bRole);

    setCompanyId(company_id);
    setCompanyRole(role);

    const { data: compRow, error: compErr } = await supabase
      .from("companies")
      .select("id, owner_id, company_name, created_at")
      .eq("id", company_id)
      .maybeSingle();

    if (compErr) {
      console.error("Error loading company record", compErr);
      setCompany(null);
    } else {
      setCompany(compRow || null);
    }

    // ✅ NEW: load company onboarding status (best effort)
    try {
      const { data: co, error: coErr } = await supabase
        .from("company_onboarding")
        .select("completed")
        .eq("company_id", company_id)
        .maybeSingle();

      if (!coErr && co?.completed) setCompanyOnboarded(true);
      else setCompanyOnboarded(false);
    } catch (e) {
      setCompanyOnboarded(false);
    }

    // ----------------------------
    // Billing: load latest row
    // ----------------------------
    let billingRow = null;

    const { data: billingByCompany, error: billingCompanyErr } = await supabase
      .from("billing")
      .select(
        "id, billing_type, billing_status, next_billing_date, payment_method, created_at, updated_at, company_id, user_id"
      )
      .eq("company_id", company_id)
      .order("created_at", { ascending: false })
      .limit(1);

    if (billingCompanyErr) {
      console.error("Error loading company billing record", billingCompanyErr);
    } else if (billingByCompany?.length) {
      billingRow = billingByCompany[0];
    }

    if (!billingRow) {
      const { data: billingByUser, error: billingUserErr } = await supabase
        .from("billing")
        .select(
          "id, billing_type, billing_status, next_billing_date, payment_method, created_at, updated_at, company_id, user_id"
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1);

      if (billingUserErr) {
        console.error("Error loading user billing record", billingUserErr);
      } else if (billingByUser?.length) {
        billingRow = billingByUser[0];
      }
    }

    let derivedPlan = "free";
    let derivedTrialEndsAt = null;
    let derivedHasUsedTrial = false;
    let derivedBillingStatus = null;
    let derivedSubscriptionEnd = null;

    const now = new Date();

    if (billingRow) {
      const rawType = normalizePlan(billingRow.billing_type);
      derivedPlan = rawType;

      derivedBillingStatus = billingRow.billing_status
        ? String(billingRow.billing_status).toLowerCase()
        : null;

      derivedSubscriptionEnd = billingRow.next_billing_date || null;

      if (derivedBillingStatus === "trial" && billingRow.next_billing_date) {
        derivedTrialEndsAt = billingRow.next_billing_date;
      }

      if (
        derivedBillingStatus === "trial" ||
        billingRow.payment_method === "paystack_trial"
      ) {
        derivedHasUsedTrial = true;
      }

      const { data: anyTrialCompany, error: anyTrialCompanyErr } =
        await supabase
          .from("billing")
          .select("id")
          .eq("company_id", company_id)
          .or("billing_status.eq.trial,payment_method.eq.paystack_trial")
          .limit(1);

      if (anyTrialCompanyErr) {
        console.error(
          "Error checking trial history (company):",
          anyTrialCompanyErr
        );
      } else if (anyTrialCompany?.length) {
        derivedHasUsedTrial = true;
      }

      if (!derivedHasUsedTrial) {
        const { data: anyTrialUser, error: anyTrialUserErr } = await supabase
          .from("billing")
          .select("id")
          .eq("user_id", user.id)
          .or("billing_status.eq.trial,payment_method.eq.paystack_trial")
          .limit(1);

        if (anyTrialUserErr) {
          console.error(
            "Error checking trial history (user):",
            anyTrialUserErr
          );
        } else if (anyTrialUser?.length) {
          derivedHasUsedTrial = true;
        }
      }

      const endDate = safeDate(billingRow.next_billing_date);

      const isPaidPlan =
        rawType === "starter" || rawType === "sme" || rawType === "business";

      const isTrial = derivedBillingStatus === "trial";

      const expired =
        endDate && endDate.getTime() < now.getTime() && (isPaidPlan || isTrial);

      if (expired) {
        derivedPlan = "free";
        derivedTrialEndsAt = null;
        derivedBillingStatus = "expired";

        try {
          await supabase
            .from("billing")
            .update({
              billing_type: "free",
              billing_status: "expired",
              updated_at: new Date().toISOString(),
            })
            .eq("id", billingRow.id);
        } catch (e) {
          console.warn(
            "Auto-downgrade: unable to update billing row (RLS?)",
            e
          );
        }
      }
    }

    setPlan(derivedPlan);
    setTrialEndsAt(derivedTrialEndsAt);
    setHasUsedTrial(derivedHasUsedTrial);
    setBillingStatus(derivedBillingStatus);
    setSubscriptionEnd(derivedSubscriptionEnd);
    setBillingRowMeta(billingRow || null);

    // Members
    const { data: memRows, error: memRowsErr } = await supabase
      .from("company_members")
      .select("user_id, invited_email, role, business_role, created_at")
      .eq("company_id", company_id)
      .order("created_at", { ascending: true });

    if (memRowsErr) {
      console.error("Error loading company members", memRowsErr);
      setMembers([]);
    } else {
      setMembers(memRows || []);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  useEffect(() => {
    if (!companyId) return;

    const channel = supabase
      .channel(`company-members-${companyId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "company_members",
          filter: `company_id=eq.${companyId}`,
        },
        () => {
          loadContext();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [companyId, loadContext]);

  const now = new Date();
  const trialEndDate = safeDate(trialEndsAt);
  const isOnTrial = trialEndDate ? trialEndDate > now : false;

  const normalizedPlan = normalizePlan(plan);

  const isFree = normalizedPlan === "free";
  const isStarter = normalizedPlan === "starter";
  const isSme = normalizedPlan === "sme";
  const isBusiness = normalizedPlan === "business" || isOnTrial;

  if (loading) {
    return <div>Loading company context…</div>;
  }

  return (
    <CompanyContext.Provider
      value={{
        company,
        companyId,
        setCompany,
        setCompanyId,
        companyRole,
        setCompanyRole,
        businessRole,
        setBusinessRole,
        effectiveRole:
          normalizedPlan === "business" ? businessRole : companyRole,
        isSuperAdmin:
          normalizedPlan === "business"
            ? businessRole === "superadmin"
            : companyRole === "admin",
        members,
        setMembers,

        // ✅ company onboarding (new)
        companyOnboarded,
        setCompanyOnboarded,

        plan: normalizedPlan,
        setPlan,
        trialEndsAt,
        setTrialEndsAt,
        hasUsedTrial,
        setHasUsedTrial,
        billingStatus,
        subscriptionEnd,
        billingRowMeta,

        isFree,
        isStarter,
        isSme,
        isBusiness,
        isOnTrial,

        refreshCompanyContext: loadContext,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error("useCompany must be inside CompanyProvider");
  return ctx;
}
